// "Speak to a specialist" directory (R-5.1). Lists TYPES of regulated professionals and official/charity
// directories only — never named firms. Topics mirror `SpecialistTopic` in @seventide/content, plus 'general'.

export type SpecialistTopic =
  | 'trusts'
  | 'annuities'
  | 'investments'
  | 'property'
  | 'equity_release'
  | 'tax'
  | 'care_fees'
  | 'legal'
  | 'welfare_benefits'
  | 'general';

export const SPECIALIST_TOPIC_LABELS: Record<SpecialistTopic, string> = {
  trusts: 'Trusts',
  annuities: 'Care fee annuities and other annuities',
  investments: 'Investments',
  property: 'Selling or transferring property',
  equity_release: 'Equity release',
  tax: 'Inheritance tax and other tax',
  care_fees: 'Paying for care',
  legal: 'Legal matters (LPAs, deputyship, wills, estates)',
  welfare_benefits: 'Benefits',
  general: 'Money guidance',
};

export type SpecialistKind = 'solla' | 'step' | 'solicitor' | 'fca' | 'ageuk' | 'moneyhelper' | 'citizensadvice';

export interface SpecialistType {
  kind: SpecialistKind;
  title: string;
  who: string;
  whenToUse: string;
  howToCheck: string;
  links: { label: string; url: string }[];
}

export const SPECIALIST_TYPES: SpecialistType[] = [
  {
    kind: 'solla',
    title: 'Later-life financial adviser (SOLLA accredited)',
    who: 'Independent financial advisers who are accredited by the Society of Later Life Advisers (SOLLA) for advice on paying for care, including care fee annuities.',
    whenToUse: 'Before deciding how to pay for care, buying a care fee annuity, or investing money to fund care.',
    howToCheck: 'Advisers must be authorised by the Financial Conduct Authority. Check the FCA register as well as SOLLA accreditation.',
    links: [
      { label: 'Society of Later Life Advisers: find an adviser', url: 'https://societyoflaterlifeadvisers.co.uk/' },
      { label: 'FCA register', url: 'https://register.fca.org.uk/' },
    ],
  },
  {
    kind: 'step',
    title: 'Trust and estate practitioner (STEP member)',
    who: 'Solicitors, accountants and other professionals who are members of the Society of Trust and Estate Practitioners (STEP). They specialise in trusts, inheritance tax and estates.',
    whenToUse: 'Setting up or running a trust, complex lifetime gifts, inheritance tax planning, or settling a larger estate.',
    howToCheck: 'Look for "TEP" after their name and check their own regulator (for example the Solicitors Regulation Authority).',
    links: [{ label: 'STEP: find a practitioner', url: 'https://www.step.org/' }],
  },
  {
    kind: 'solicitor',
    title: 'Solicitor',
    who: 'Regulated legal professionals. Some specialise in elderly client law, Court of Protection work, wills and probate.',
    whenToUse: 'Lasting Powers of Attorney, deputyship, wills, property transfers, disputes about care funding, and probate.',
    howToCheck:
      'In England and Wales use the Law Society directory. In Scotland use the Law Society of Scotland, and in Northern Ireland the Law Society of Northern Ireland.',
    links: [
      { label: 'Law Society: find a solicitor (England and Wales)', url: 'https://solicitors.lawsociety.org.uk/' },
      { label: 'Law Society of Scotland: find a solicitor', url: 'https://www.lawscot.org.uk/find-a-solicitor/' },
      { label: 'Law Society of Northern Ireland', url: 'https://www.lawsoc-ni.org/' },
    ],
  },
  {
    kind: 'fca',
    title: 'FCA-authorised financial adviser',
    who: 'Financial advisers authorised by the Financial Conduct Authority. Only authorised firms may give regulated advice on investments, annuities, pensions and equity release.',
    whenToUse: 'Investments, pensions, annuities, and equity release (equity release advice must come from an authorised adviser).',
    howToCheck: 'Search the FCA register for the firm and the individual adviser before you go ahead.',
    links: [{ label: 'FCA register search', url: 'https://register.fca.org.uk/' }],
  },
  {
    kind: 'moneyhelper',
    title: 'MoneyHelper (free, government-backed guidance)',
    who: 'Free and impartial money and pension guidance backed by the government. Includes guides on paying for care and a directory of retirement advisers.',
    whenToUse: 'A good first step for understanding your options, and for finding a regulated adviser.',
    howToCheck: 'MoneyHelper is run by the Money and Pensions Service.',
    links: [{ label: 'MoneyHelper', url: 'https://www.moneyhelper.org.uk/' }],
  },
  {
    kind: 'ageuk',
    title: 'Age UK (free advice)',
    who: 'A charity offering free, confidential advice on care, benefits, money and legal questions for older people and their families.',
    whenToUse: 'Questions about care assessments, benefits such as Attendance Allowance, and where to get further help.',
    howToCheck: 'Age UK is a registered charity. Local Age UK branches may also offer help in person.',
    links: [{ label: 'Age UK advice', url: 'https://www.ageuk.org.uk/' }],
  },
  {
    kind: 'citizensadvice',
    title: 'Citizens Advice (free advice)',
    who: 'A charity offering free advice on benefits, money, housing and legal problems.',
    whenToUse: 'Benefits claims and appeals, debt, and problems with councils or care providers.',
    howToCheck: 'Citizens Advice is a registered charity.',
    links: [{ label: 'Citizens Advice', url: 'https://www.citizensadvice.org.uk/' }],
  },
];

/** Which professional types fit a topic, most relevant first. */
export const SPECIALISTS_FOR_TOPIC: Record<SpecialistTopic, SpecialistKind[]> = {
  trusts: ['step', 'solicitor', 'moneyhelper'],
  annuities: ['solla', 'fca', 'moneyhelper'],
  investments: ['fca', 'solla', 'moneyhelper'],
  property: ['solicitor', 'solla', 'step'],
  equity_release: ['fca', 'solla', 'moneyhelper'],
  tax: ['step', 'solicitor', 'moneyhelper'],
  care_fees: ['solla', 'ageuk', 'moneyhelper', 'solicitor'],
  legal: ['solicitor', 'step', 'ageuk'],
  welfare_benefits: ['ageuk', 'citizensadvice', 'moneyhelper'],
  general: ['moneyhelper', 'ageuk', 'citizensadvice', 'fca'],
};

export function isSpecialistTopic(v: string | null | undefined): v is SpecialistTopic {
  return !!v && v in SPECIALIST_TOPIC_LABELS;
}

/** Path of the in-app "Speak to a specialist" page for a topic (inside a workspace when wsId is given). */
export function specialistPath(wsId: string | undefined, topic: SpecialistTopic): string {
  const base = wsId ? `/w/${wsId}/more/help/specialist` : '/specialist';
  return `${base}?topic=${encodeURIComponent(topic)}`;
}
